// src/helpers/rehypeGroupWebCites.ts

/**
 * Rehype plugin that merges a run of adjacent web <cite> elements into one.
 *
 * The web counterpart of rehypeGroupDocCites, for Gemini's citations only: a
 * claim backed by several web sources would otherwise render as a row of chips.
 * Only tags marked `data-grounded` (spliced in from Google's grounding) take
 * part, so Claude's citations render exactly as they always have. A run is
 * empty grounded <cite> siblings separated only by whitespace; it is folded
 * into its FIRST element, which gets the run's sources in order, de-duplicated by URL, as a JSON
 * `data-sources` attribute ([{ source, url, title? }]). The others and the whitespace
 * between them are removed. A single citation is left exactly as it was.
 *
 * Folding into the first element keeps its key (`cite-N`) stable, as
 * rehypeGroupDocCites does for document citations.
 *
 * Must run after rehype-raw: before it, the tags are still raw nodes.
 */

type HastNode = {
  type: string;
  tagName?: string;
  value?: string;
  properties?: Record<string, unknown>;
  children?: HastNode[];
};

export interface WebCiteSource {
  source: string;
  url: string;
  /** The page's own title, when Google gave one */
  title?: string;
}

const WHITESPACE_ONLY = /^\s*$/;

/** A grounded citation chip, not a <cite> wrapping text (that renders its children). */
const isWebCite = (node: HastNode): boolean =>
  node.type === "element" &&
  node.tagName === "cite" &&
  !node.children?.length &&
  node.properties?.dataGrounded === "true";

const sourceOf = (node: HastNode): WebCiteSource => {
  const title = node.properties?.dataTitle;

  return {
    source: String(node.properties?.source ?? ""),
    url: String(node.properties?.url ?? ""),
    ...(typeof title === "string" && title ? { title } : {}),
  };
};

const groupSiblings = (parent: HastNode): void => {
  const children = parent.children;

  if (!children) return;

  for (let i = 0; i < children.length; i++) {
    const node = children[i];

    if (!isWebCite(node)) {
      groupSiblings(node);
      continue;
    }

    // Only this plugin sets data-sources; one written into the tag itself is dropped.
    if (node.properties) delete node.properties.dataSources;

    const sources = [sourceOf(node)];
    let lastInRun = i;

    for (let j = i + 1; j < children.length; j++) {
      const next = children[j];

      if (next.type === "text" && WHITESPACE_ONLY.test(next.value ?? "")) continue;
      if (!isWebCite(next)) break;
      sources.push(sourceOf(next));
      lastInRun = j;
    }

    if (lastInRun > i) {
      const seen = new Set<string>();
      const unique = sources.filter(({ url }) => !url || (!seen.has(url) && !!seen.add(url)));

      node.properties = { ...node.properties, dataSources: JSON.stringify(unique) };
      // Whitespace after the last tag in the run stays; it separates the chip
      // from the text that follows.
      children.splice(i + 1, lastInRun - i);
    }
  }
};

export const rehypeGroupWebCites = () => (tree: HastNode) => {
  groupSiblings(tree);
};

export default rehypeGroupWebCites;
